import { Injectable, Injector, NgZone, OnDestroy } from '@angular/core';
import { Platform } from '@ionic/angular';
import { ToastService } from '../../shared/services/toast/toast.service';
import { ProfileService } from './profile.service';
import { getToken, isSupported, Messaging } from '@angular/fire/messaging';
import { Observable, Subject } from 'rxjs';
import { PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import PushNotificationElectron from 'src/app/plugins/push-notification.plugin';
import { environment } from 'src/environments/environment';
import { isElectron, isNativePlatform } from 'src/app/shared/utils/platform.utils';
import { SessionService } from './session.service';
import { Router } from '@angular/router';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { IMultiTableTournamentState, ISitNGoTournamentState } from '@soposopo/sopo-models';
import { SopoPopupService } from 'src/app/sopo-popup/service/sopo-popup.service';
import { AuthStoreDispatcher } from '../state/auth/auth.dispatcher';
import { FreePlayLobbyService } from './free-play.service';
import { LobbyService } from 'src/app/lobby/pages/lobby-home/lobby.service';

interface NotificationClickData {
    tournamentId?: string;
    clubId?: string;
    body?: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy{
    public messaging: Messaging;
    private _notificationClickHandler: ((_: any, data: NotificationClickData) => void) | null = null;

    constructor(
        private platform: Platform,
        private toastService: ToastService,
        public profileService: ProfileService,
        private injector: Injector,
        private sessionService: SessionService,
        private router: Router,
        private zone: NgZone,
        private authStoreDispatcher: AuthStoreDispatcher,
        private freePlayService: FreePlayLobbyService,
        private sopoPopupService: SopoPopupService,
        private firestore: Firestore,
        private lobbyService: LobbyService,
    ) {
        if (isNativePlatform()) {
            this.onNotifications().subscribe((msg) => {
                if (this.platform.is('ios')) {
                    this.toastService.toast(msg['aps'] ? msg['aps'].alert.body : msg.body);
                } else {
                    if (msg.body) this.toastService.toast(msg.body);
                }
            });
        }
        isSupported().then((supported) => {
            if(supported) {
                this.messaging = injector.get(Messaging);
                // onMessage(this.messaging ,message => {
                //     this.toastService.toast(message.notification.body, 3000, "bottom");
                // });
            }
        })

        if (isElectron()) {
            this.initElectronNotificationClickHandler();
        }
    }

    async getToken(): Promise<string | null> {
        if (isNativePlatform()) {
            return new Promise((resolve, reject) => {
                if (isElectron()) {
                    PushNotificationElectron.register({
                        apiKey: environment.firebaseConfig.apiKey,
                        appID: environment.firebaseConfig.appId,
                        projectID: environment.firebaseConfig.projectId
                    }).then(token => resolve(token))
                    .catch(error => {
                        console.error(error);
                        reject(null);
                    });
                } else {
                    PushNotifications.requestPermissions().then((result) => {
                        if (result.receive === 'granted') {
                            PushNotifications.register();
                        }
                    });
                    PushNotifications.addListener('registration', (token: Token) => {
                        resolve(token.value);
                    });

                    PushNotifications.addListener('registrationError', (error: any) => {
                        console.error('Error on registration: ' + JSON.stringify(error));
                        reject(null);
                    });
                }
            });
        } else {
            return getToken(this.messaging).then((token) => {
                return token;
            });
        }
    }

    onNotifications() {
        const notiFicationSubject = new Subject<PushNotificationSchema>();
        if (!isElectron()) {
            PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
                console.log(notification)
                notiFicationSubject.next(notification)
            });
        }
        return notiFicationSubject;
    }

    // subscribeTopic(topic, token): Promise<void> {
    //     return httpsCallable(this.fun, 'subscribeToTopic')({
    //             topic: topic,
    //             token: token,
    //         })
    //         .then(() => console.log(`Subscribed from ${topic}`));
    // }

    // unsubscribeTopic(topic, token): Promise<void> {
    //     return httpsCallable(this.fun, 'unsubscribeFromTopic')({
    //             topic: topic,
    //             token: token,
    //         })
    //         .then(() => console.log(`Unsubscribed from ${topic}`));
    // }

    sendTestNotification(token): Promise<any> {
        const promise = new Promise((resolve, reject) => {
            resolve("Success!");
        })
        // return new Promise((resolve, reject) => {
        //     this.subscribeTopic('testing', token).add(() => {
        //             httpsCallable(this.fun, 'testPushNotification')({
        //                 token: token,
        //             })
        //             .then(() => console.log(`sent test notification`))
        //             .add(() => {
        //                 this.unsubscribeTopic('testing', token);
        //                 resolve(token);
        //             });
        //     });
        // });
        return promise;
    }

    persistFCMToken(args: { userID: string; token: string }): Observable<string> {
        return Observable.create((observer) => {
            this.updateFcmToken(args.token, args.userID)
                .then(() => {
                    // Update returns void so we have to send the token back again...
                    observer.next(args.token);
                    observer.complete();
                });
        });
    }

    async updateFcmToken(token: string, userID: string) {
        try {
            const sessionId = this.sessionService.getStoredSessionId();
            if (sessionId) {
                await this.sessionService.updateSessionField(userID, sessionId, { fcmToken: token });
            }
        } catch (error) {
            console.error(error);
        }
    }

    private initElectronNotificationClickHandler() {
        if (window.name && window.name.includes('gameTab-')) return;
        this._notificationClickHandler = (_, data: NotificationClickData) => {
            this.zone.run(() => this.handleNotificationClick(data));
        };

        window.electron.ipcRenderer.on('notification-clicked', this._notificationClickHandler);
    }

    private async handleNotificationClick(data: NotificationClickData) {
        const { tournamentId, clubId, body } = data;

        if (!tournamentId) {
            if (clubId && clubId !== 'SOPO') this.router.navigate(['/club-lobby', clubId]);
            return;
        }

        const isJackpotSng = body?.toLowerCase().includes('jackpot sng') || body?.toLowerCase().includes('super spin');
        const tournamentData = await this.getTournamentData(tournamentId);

        if (isJackpotSng) {
            if (!tournamentData?.rules) return;

            const gameConfigId = tournamentData.gameConfigId;
            const userId = await this.authStoreDispatcher.getMyID();

            if (gameConfigId && userId) {
                try {
                    await this.freePlayService.enterTable(tournamentId, true, userId, gameConfigId);
                } catch (error) {
                    this.sopoPopupService.showAlert('app', {
                        header: 'Error entering tournament',
                        message: `An unexpected error occurred: ${error.message || error}`,
                    });
                }
            }
            return;
        }

        if (tournamentId && clubId && tournamentData) {
            this.router.navigate(['/tournament-registration', clubId, tournamentId]);
            switch (tournamentData.currency) {
                case 'chip':
                    this.lobbyService.setActiveLobbyMenu('clubs');
                    break;
                case 'golds':
                    this.lobbyService.setActiveLobbyMenu('gold');
                    break;
                case 'sweepbucks':
                    this.lobbyService.setActiveLobbyMenu('sweeps');
                    break;
                case 'none':
                    this.lobbyService.setActiveLobbyMenu('quickplay');
                    break;
                default:
                    this.lobbyService.setActiveLobbyMenu('sweeps');
            }
        }
    }

    private async getTournamentData(tournamentId: string): Promise<IMultiTableTournamentState | ISitNGoTournamentState | undefined> {
        const docRef = doc(this.firestore, `tournaments/${tournamentId}`);
        return (await getDoc(docRef)).data() as IMultiTableTournamentState | ISitNGoTournamentState | undefined;
    }

    ngOnDestroy() {
        if (isElectron() && this._notificationClickHandler) {
            window.electron.ipcRenderer.removeListener('notification-clicked', this._notificationClickHandler);
            this._notificationClickHandler = null;
        }
    }
}
